import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Logo, Input, Select, Button, Empty, Badge } from "@/lib/ui";
import { useAuth } from "@/context/Auth";
import ProductCard from "@/components/ProductCard";

const SORTS = [
  { v: "az", t: "A → Z" },
  { v: "newest", t: "Newest" },
  { v: "discount", t: "Biggest Discount" },
  { v: "price_low", t: "Price: Low → High" },
  { v: "price_high", t: "Price: High → Low" },
  { v: "most_viewed", t: "Most Viewed" },
];

export default function Marketplace() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { user, logout } = useAuth();
  const [filters, setFilters] = useState({ categories: [], sizes: [], cities: [], localities: [] });
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [visits, setVisits] = useState(null);
  const [q, setQ] = useState({ search: "", category: "", size: "", city: params.get("city") || "", locality: "", pan_india: false, sort: "az", condition: "" });

  useEffect(() => {
    api.get("/public/filters").then(({ data }) => setFilters(data)).catch(() => {});
    const done = localStorage.getItem("ds_visited_marketplace");
    const req = done ? api.get("/public/stats") : api.post("/public/visit");
    if (!done) localStorage.setItem("ds_visited_marketplace", "1");
    req.then(({ data }) => setVisits(data.marketplace_visits)).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(q).forEach(([k, v]) => { if (v !== "" && v !== false) params[k] = v; });
      const { data } = await api.get("/marketplace/products", { params });
      setProducts(data.products);
      setTotal(data.total);
    } finally {
      setLoading(false);
    }
  }, [q]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const set = (k, v) => setQ((p) => ({ ...p, [k]: v, ...(k === "city" ? { locality: "" } : {}) }));
  const reset = () => setQ({ search: "", category: "", size: "", city: "", locality: "", pan_india: false, sort: "az", condition: "" });

  const locOptions = filters.localities.filter((l) => !q.city || l.city === q.city);

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-7xl items-center gap-3 px-4 py-3">
          <button onClick={() => nav("/")}><Logo /></button>
          <div className="relative ml-2 hidden flex-1 md:block">
            <i className="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="marketplace-search-input"
              value={q.search}
              onChange={(e) => set("search", e.target.value)}
              placeholder="Search products, categories, sellers…"
              className="w-full rounded-full border border-slate-300 py-2 pl-10 pr-4 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
            />
          </div>
          <div className="ml-auto flex items-center gap-2">
            {user?.role === "buyer" && <Badge tone="slate"><i className="fa-solid fa-user" /> {user.phone}</Badge>}
            <Button variant="ghost" onClick={() => { logout(); nav("/"); }} data-testid="marketplace-exit-btn">
              <i className="fa-solid fa-arrow-right-from-bracket" /> Exit
            </Button>
          </div>
        </div>
        <div className="border-t border-slate-100 px-4 py-2 md:hidden">
          <input
            value={q.search}
            onChange={(e) => set("search", e.target.value)}
            placeholder="Search stock…"
            className="w-full rounded-full border border-slate-300 py-2 px-4 text-sm outline-none"
          />
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-4 py-6">
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <Select data-testid="filter-category" value={q.category} onChange={(e) => set("category", e.target.value)} className="w-auto">
            <option value="">All Categories</option>
            {filters.categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
          </Select>
          <Select data-testid="filter-city" value={q.city} onChange={(e) => set("city", e.target.value)} className="w-auto">
            <option value="">All Cities</option>
            {filters.cities.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
          </Select>
          <Select data-testid="filter-locality" value={q.locality} onChange={(e) => set("locality", e.target.value)} className="w-auto" disabled={!q.city}>
            <option value="">All Localities</option>
            {locOptions.map((l) => <option key={l.id} value={l.name}>{l.name}</option>)}
          </Select>
          <Select data-testid="filter-size" value={q.size} onChange={(e) => set("size", e.target.value)} className="w-auto">
            <option value="">All Sizes</option>
            {filters.sizes.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
          </Select>
          <Select data-testid="filter-condition" value={q.condition} onChange={(e) => set("condition", e.target.value)} className="w-auto">
            <option value="">Any Condition</option>
            <option>New</option><option>Used</option><option>Refurbished</option>
          </Select>
          <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm">
            <input data-testid="filter-panindia" type="checkbox" checked={q.pan_india} onChange={(e) => set("pan_india", e.target.checked)} />
            Pan India
          </label>
          <Select data-testid="filter-sort" value={q.sort} onChange={(e) => set("sort", e.target.value)} className="w-auto">
            {SORTS.map((s) => <option key={s.v} value={s.v}>{s.t}</option>)}
          </Select>
          <Button variant="outline" onClick={reset} data-testid="filter-reset"><i className="fa-solid fa-rotate-left" /> Reset</Button>
        </div>

        {visits > 0 && (
          <p className="mb-2 inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700" data-testid="marketplace-visits">
            <i className="fa-solid fa-users" /> {visits.toLocaleString("en-IN")} people visited the marketplace till date
          </p>
        )}
        <p className="mb-3 text-sm text-slate-500">
          {loading ? "Loading stock…" : <><span className="font-bold text-slate-900">{total}</span> live listings found</>}
        </p>

        {!loading && products.length === 0 ? (
          <Empty text="No stock matches your filters yet" icon="fa-boxes-stacked" />
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 md:grid-cols-4 lg:grid-cols-5 lg:gap-6">
            {products.map((p) => <ProductCard key={p.id} p={p} />)}
          </div>
        )}
      </div>
    </div>
  );
}
